/* eslint-disable react/jsx-props-no-spreading */
import { AngleLeft, AngleRight, VerticalEllipsis } from '@/client/components/icons/ContinuIcons';
import { Button, ButtonGroup, Flex, HStack, IconButton } from '@chakra-ui/react';
import { PaginationState, Table } from '@tanstack/react-table';

interface ScrollingPaginationProps {
  table: Table<any>;
  pagination: PaginationState;
  pagesArr: { label: string; value: number }[];
  paddingX?: number;
  paddingY?: number;
  positionStyles?: {
    position?: 'fixed' | 'sticky' | 'absolute' | 'unset';
    bottom?: number;
    left?: number;
    right?: number;
    top?: number;
    paddingX?: number;
    paddingY?: number;
  };
}

export default function ScrollingPagination({
  table,
  pagination,
  pagesArr,
  paddingX = 10,
  paddingY = 2,
  positionStyles = {
    position: 'fixed',
    bottom: 0,
    left: 50,
    right: 0,
  },
}: ScrollingPaginationProps) {
  if (pagesArr.length === 0) return null;

  const currentPage = pagination.pageIndex;
  const lastIndex = pagesArr.length - 1;
  // 5-page window around the current page; the first and last pages render as
  // pinned buttons outside it so they're always one click away.
  const startIndex = Math.floor(currentPage / 5) * 5;
  const endIndex = startIndex + 5;

  const pageButton = (
    page: { label: string; value: number },
    index: number
  ) => (
    <Button
      variant="paginationNumber"
      key={page.value}
      onClick={() => table.setPageIndex(page.value)}
      isActive={index === currentPage}
      _active={{ borderColor: 'brand.button', color: 'baseBlack' }}
    >
      {page.label}
    </Button>
  );

  const ellipsis = (
    <Flex
      height="32px"
      width="32px"
      direction="column"
      alignItems="center"
      justifyContent="flex-end"
    >
      <VerticalEllipsis transform="rotate(90deg)" color="baseBlack" />
    </Flex>
  );

  return (
    <Flex
      {...positionStyles}
      paddingX={paddingX}
      paddingY={paddingY}
      backgroundColor="neutral.0"
    >
      <ButtonGroup alignItems="center">
        <IconButton
          variant="paginationArrow"
          icon={<AngleLeft />}
          aria-label="Previous page"
          isDisabled={!table.getCanPreviousPage()}
          onClick={() => table.previousPage()}
        />

        {pageButton(pagesArr[0], 0)}

        {startIndex > 1 && ellipsis}

        <HStack overflowX="scroll" maxWidth={60}>
          {pagesArr.map((page, index) => {
            // Window only — the pinned first/last buttons cover the ends.
            if (index === 0 || index === lastIndex) return null;
            if (index < startIndex || index >= endIndex) return null;
            return pageButton(page, index);
          })}
        </HStack>

        {endIndex < lastIndex && ellipsis}

        {lastIndex > 0 && pageButton(pagesArr[lastIndex], lastIndex)}

        <IconButton
          variant="paginationArrow"
          icon={<AngleRight />}
          aria-label="Next page"
          isDisabled={!table.getCanNextPage()}
          onClick={() => table.nextPage()}
        />
      </ButtonGroup>
    </Flex>
  );
}
